import { useEffect, useState } from "react";
import { core } from "@servicetitan/hammer-token";

/**
 * Return type for the useBreakpoint hook
 */
export type BreakpointReturnProps = {
  /**
   * The breakpoint name
   */
  name: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  /**
   * Minimum width for this breakpoint
   */
  min: number | undefined;
  /**
   * Maximum width for this breakpoint
   */
  max: number | undefined;
  /**
   * Current device orientation
   */
  orientation: "portrait" | "landscape";
  /**
   * Current viewport width
   */
  innerWidth: number;
  /**
   * Current viewport height
   */
  innerHeight: number;
};

/**
 * Determines if the current viewport matches a specific breakpoint
 * @param name - The breakpoint name
 * @param min - Minimum width for this breakpoint
 * @param max - Maximum width for this breakpoint
 * @returns Breakpoint information if matched, undefined otherwise
 */
function breakpoint(
  name: BreakpointReturnProps["name"],
  min?: number,
  max?: number,
): BreakpointReturnProps | undefined {
  if (globalThis.visualViewport == null) {
    return undefined;
  }

  if (
    (min == null || globalThis.visualViewport?.width >= min) &&
    (max == null || globalThis.visualViewport?.width < max)
  ) {
    return {
      name,
      min,
      max,
      orientation:
        globalThis.visualViewport?.height > globalThis.visualViewport?.width
          ? "portrait"
          : "landscape",
      innerWidth: globalThis.visualViewport?.width,
      innerHeight: globalThis.visualViewport?.height,
    } as const;
  }

  return undefined;
}

/**
 * Determines the current breakpoint based on viewport width and hammer-token breakpoint values.
 *
 * Features:
 * - Validates that all required hammer-token breakpoint values are available
 * - Uses cascade logic to determine the appropriate breakpoint
 * - Returns the first matching breakpoint in order of preference
 * - Handles edge cases where breakpoint values are missing
 * - Uses visualViewport API for accurate viewport measurements
 * - Supports all standard breakpoints (xs, sm, md, lg, xl, xxl)
 *
 * @returns Current breakpoint information or undefined if breakpoint values are unavailable
 */
const onResize = () => {
  if (!core.primitive?.BreakpointSm) return;
  if (!core.primitive?.BreakpointMd) return;
  if (!core.primitive?.BreakpointLg) return;
  if (!core.primitive?.BreakpointXl) return;
  if (!core.primitive?.BreakpointXxl) return;

  const sm = parseInt(core.primitive?.BreakpointSm.value);
  const md = parseInt(core.primitive?.BreakpointMd.value);
  const lg = parseInt(core.primitive?.BreakpointLg.value);
  const xl = parseInt(core.primitive?.BreakpointXl.value);
  const xxl = parseInt(core.primitive?.BreakpointXxl.value);

  return (
    breakpoint("xs", undefined, sm) ??
    breakpoint("sm", sm, md) ??
    breakpoint("md", md, lg) ??
    breakpoint("lg", lg, xl) ??
    breakpoint("xl", xl, xxl) ??
    breakpoint("xxl", xxl, undefined)
  );
};

/**
 * Custom hook for detecting current breakpoint and viewport information.
 *
 * Features:
 * - Detects current breakpoint based on viewport width
 * - Supports all standard breakpoints (xs, sm, md, lg, xl, xxl)
 * - Provides device orientation information
 * - Returns current viewport dimensions
 * - Uses hammer-token breakpoint values for consistency
 * - Listens for window resize events
 * - Supports optional disable functionality
 * - Uses visualViewport API for accurate measurements
 *
 * @param props - Optional configuration object
 * @param props.disable - Whether to disable the hook
 * @returns Current breakpoint information or undefined if disabled
 */
export const useBreakpoint = (props?: { disable?: boolean }) => {
  const [size, setSize] = useState(onResize());

  useEffect(() => {
    if (props?.disable) return;

    const updateSize = () => {
      setSize(onResize());
    };
    globalThis.addEventListener("resize", updateSize);

    return () => {
      globalThis.removeEventListener("resize", updateSize);
    };
  }, []);

  if (props?.disable) return;

  return size;
};
